import { Injectable, inject } from '@angular/core';
import { collection, doc, getDoc, getDocs, deleteDoc, setDoc, serverTimestamp, query, where, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { Role, ALL_PERMISSIONS, Permission, RoleInfo, AdUser, SYSTEM_ROLE_DEFINITIONS } from '../models/role.model';
import { FirebaseService } from './firebase.service';

@Injectable({
  providedIn: 'root',
})
export class RoleService {
  private readonly firebase = inject(FirebaseService);
  private readonly rolesCache = new Map<string, Role[]>();
  private readonly roleCache = new Map<string, Role>();

  async listRoles(orgId: string, forceRefresh = false): Promise<Role[]> {
    const cacheKey = `crewdle_roles_${orgId}`;

    if (!forceRefresh) {
      if (this.rolesCache.has(cacheKey)) return this.rolesCache.get(cacheKey)!;
      const stored = sessionStorage.getItem(cacheKey);
      if (stored) {
        const roles = JSON.parse(stored) as Role[];
        this.rolesCache.set(cacheKey, roles);
        return roles;
      }
    }

    const rolesRef = collection(this.firebase.firestore, 'organizations', orgId, 'roles');
    const snapshot = await getDocs(rolesRef);
    const roles = snapshot.docs.map((d) => this.mapRoleData(d));
    this.rolesCache.set(cacheKey, roles);
    sessionStorage.setItem(cacheKey, JSON.stringify(roles));
    return roles;
  }

  async getRole(orgId: string, roleId: string): Promise<Role> {
    const cacheKey = `crewdle_role_${orgId}_${roleId}`;

    if (this.roleCache.has(cacheKey)) return this.roleCache.get(cacheKey)!;

    const stored = sessionStorage.getItem(cacheKey);
    if (stored) {
      const role = JSON.parse(stored) as Role;
      this.roleCache.set(cacheKey, role);
      return role;
    }

    const roleRef = doc(this.firebase.firestore, 'organizations', orgId, 'roles', roleId);
    const snapshot = await getDoc(roleRef);
    if (!snapshot.exists()) {
      throw new Error(`Role ${roleId} not found`);
    }
    const role = this.mapRoleData(snapshot);
    this.roleCache.set(cacheKey, role);
    sessionStorage.setItem(cacheKey, JSON.stringify(role));
    return role;
  }

  private invalidateRoles(orgId: string): void {
    const listKey = `crewdle_roles_${orgId}`;
    this.rolesCache.delete(listKey);
    sessionStorage.removeItem(listKey);
    // Individual role entries are bust lazily on next read
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith(`crewdle_role_${orgId}_`)) sessionStorage.removeItem(key);
    }
    for (const key of this.roleCache.keys()) {
      if (key.startsWith(`crewdle_role_${orgId}_`)) this.roleCache.delete(key);
    }
  }

  async createRole(orgId: string, role: Partial<Role>): Promise<Role> {
    const userId = this.firebase.auth.currentUser?.uid || '';
    const roleRef = doc(collection(this.firebase.firestore, 'organizations', orgId, 'roles'));

    await setDoc(roleRef, {
      name: role.name || '',
      description: role.description || '',
      permissions: role.permissions || [],
      isSystem: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userId,
    });

    this.invalidateRoles(orgId);
    return {
      id: roleRef.id,
      name: role.name || '',
      description: role.description || '',
      permissions: role.permissions || [],
      isSystem: false,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: userId,
    };
  }

  async updateRole(orgId: string, roleId: string, permissions: string[]): Promise<void> {
    const callable = httpsCallable<
      { organizationId: string; roleId: string; permissions: string[] },
      { success: boolean }
    >(this.firebase.functions, 'updateOrganizationRole');

    await callable({ organizationId: orgId, roleId, permissions });
    this.invalidateRoles(orgId);
  }

  async updateRoleDetails(orgId: string, roleId: string, data: { name?: string; description?: string; permissions?: string[]; isSystem?: boolean }): Promise<void> {
    const roleRef = doc(this.firebase.firestore, 'organizations', orgId, 'roles', roleId);
    const update: Record<string, any> = { updatedAt: serverTimestamp() };
    if (data.name !== undefined) update['name'] = data.name;
    if (data.description !== undefined) update['description'] = data.description;
    if (data.permissions !== undefined) update['permissions'] = data.permissions;
    if (data.isSystem !== undefined) update['isSystem'] = data.isSystem;
    await setDoc(roleRef, update, { merge: true });
    this.invalidateRoles(orgId);
  }

  async deleteRole(orgId: string, roleId: string): Promise<void> {
    // Prevent system role deletion
    const role = await this.getRole(orgId, roleId);
    if (role.isSystem) {
      throw new Error('Cannot delete system roles');
    }
    await this.reassignMembersToMember(orgId, roleId);
    const roleRef = doc(this.firebase.firestore, 'organizations', orgId, 'roles', roleId);
    await deleteDoc(roleRef);
    this.invalidateRoles(orgId);
  }

  /**
   * Before deleting a custom role, reassign any member who has it to the
   * default 'member' role so the UI doesn't show a dangling role id.
   */
  private async reassignMembersToMember(orgId: string, roleId: string): Promise<void> {
    const membersRef = collection(this.firebase.firestore, 'organizations', orgId, 'members');
    const [byRoleId, byRole] = await Promise.all([
      getDocs(query(membersRef, where('roleId', '==', roleId))),
      getDocs(query(membersRef, where('role', '==', roleId))),
    ]);

    const seen = new Set<string>();
    const batch = writeBatch(this.firebase.firestore);
    for (const snap of [...byRoleId.docs, ...byRole.docs]) {
      if (seen.has(snap.id)) continue;
      seen.add(snap.id);
      batch.set(snap.ref, { role: 'member', roleId: 'member' }, { merge: true });
    }
    if (seen.size > 0) await batch.commit();
  }

  async seedSystemRoles(orgId: string): Promise<void> {
    const callable = httpsCallable<{ organizationId: string }, { success: boolean }>(
      this.firebase.functions,
      'seedOrganizationRoles',
    );
    await callable({ organizationId: orgId });
  }

  async getAdUsersAndRoles(orgId: string): Promise<{ adManaged: boolean; roles: RoleInfo[]; users: AdUser[] }> {
    const callable = httpsCallable<
      { organizationId: string },
      { adManaged: boolean; roles: RoleInfo[]; users: AdUser[] }
    >(this.firebase.functions, 'getAdUsersAndRoles');

    const result = await callable({ organizationId: orgId });
    return result.data;
  }

  getAvailablePermissions(): Permission[] {
    return ALL_PERMISSIONS;
  }

  async seedSystemRolesIfMissing(orgId: string): Promise<void> {
    const seededKey = `crewdle_roles_seeded_${orgId}`;
    if (sessionStorage.getItem(seededKey)) return;

    for (const def of SYSTEM_ROLE_DEFINITIONS) {
      const roleRef = doc(this.firebase.firestore, 'organizations', orgId, 'roles', def.id);
      const snapshot = await getDoc(roleRef);
      if (!snapshot.exists()) {
        await setDoc(roleRef, {
          name: def.name,
          description: def.description,
          permissions: def.permissions,
          isSystem: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: 'system',
        });
      }
    }

    sessionStorage.setItem(seededKey, '1');
    this.invalidateRoles(orgId);
  }

  private mapRoleData(snapshot: any): Role {
    const data = snapshot.data();
    return {
      id: snapshot.id,
      name: data['name'] || snapshot.id,
      description: data['description'] || '',
      permissions: data['permissions'] || [],
      isSystem: data['isSystem'] ?? false,
      createdAt: data['createdAt'],
      updatedAt: data['updatedAt'],
      createdBy: data['createdBy'] || '',
    };
  }
}
